import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { SaveFilterModal } from './SaveFilterModal';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { toast } from 'sonner';
import { savedFiltersService } from '@/services/saved-filters-service';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';

// Mock data for dropdowns
const ACTION_TYPES = [
  'Asset Created',
  'Asset Updated',
  'Asset Deleted',
  'Status Changed',
  'Location Changed',
  'Assigned To Changed',
  'Depreciation Applied',
  'Warranty Updated',
  'Maintenance Scheduled',
  'Audit Performed',
];

const USERS = [
  'John Smith',
  'Jane Doe',
  'Bob Johnson',
  'Alice Williams',
  'Charlie Brown',
  'System',
];

const createEmptyFilters = (): Partial<PendingFilters> => ({
  auditTrailAssetCode: '',
  auditTrailAssetDescription: '',
  auditTrailDateRange: { from: undefined, to: undefined },
  auditTrailActionTypes: [],
  auditTrailUsers: [],
});

export const AuditTrailFiltersTab: React.FC = () => {
  const {
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearPendingFilters,
    clearAllFilters,
    savedFilters,
    setSavedFilters,
    loadSavedFilter,
  } = useRightPanel();

  // Local state for filter inputs
  const [assetCode, setAssetCode] = useState('');
  const [assetDescription, setAssetDescription] = useState('');
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [selectedActionTypes, setSelectedActionTypes] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Save filter modal state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // Selected saved filter
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);

  // Loading state for API operations
  const [isSaving, setIsSaving] = useState(false);

  // Ref to track if we're loading from external source (to prevent circular updates)
  const isLoadingFromExternal = React.useRef(false);

  // Load saved filters from API on component mount
  const loadSavedFiltersFromApi = useCallback(async () => {
    try {
      const filters = await savedFiltersService.getSavedFilters();
      setSavedFilters(filters);
    } catch (error) {
      console.error('Failed to load saved filters:', error);
      toast.error('Failed to load saved filters');
    }
  }, [setSavedFilters]);

  // Load saved filters on mount
  useEffect(() => {
    loadSavedFiltersFromApi();
  }, [loadSavedFiltersFromApi]);

  // Update pending filters whenever local state changes (but not when loading from external)
  useEffect(() => {
    if (isLoadingFromExternal.current) {
      return;
    }

    const filters: Partial<PendingFilters> = {
      auditTrailAssetCode: assetCode,
      auditTrailAssetDescription: assetDescription,
      auditTrailDateRange: dateRange,
      auditTrailActionTypes: selectedActionTypes,
      auditTrailUsers: selectedUsers,
    };
    setPendingFilters(filters as PendingFilters);
  }, [assetCode, assetDescription, dateRange, selectedActionTypes, selectedUsers, setPendingFilters]);

  // Load filters from context when they change externally
  useEffect(() => {
    if (!pendingFilters) return;

    isLoadingFromExternal.current = true;

    setAssetCode(pendingFilters.auditTrailAssetCode ?? '');
    setAssetDescription(pendingFilters.auditTrailAssetDescription ?? '');
    setDateRange(pendingFilters.auditTrailDateRange ?? { from: undefined, to: undefined });
    setSelectedActionTypes(pendingFilters.auditTrailActionTypes ?? []);
    setSelectedUsers(pendingFilters.auditTrailUsers ?? []);

    // Reset the flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isLoadingFromExternal.current = false;
    }, 0);
  }, [pendingFilters]);

  // Calculate active filter count
  const activeFilterCount =
    (assetCode ? 1 : 0) +
    (assetDescription ? 1 : 0) +
    (dateRange.from || dateRange.to ? 1 : 0) +
    selectedActionTypes.length +
    selectedUsers.length;

  // Update the context with the active filter count
  useEffect(() => {
    setActiveFilterCount(activeFilterCount);
  }, [activeFilterCount, setActiveFilterCount]);

  // Listen for external clear all filters (from header button)
  useEffect(() => {
    // If both applied and pending filters are null but we have local filter state,
    // it means clearAllFilters was called from outside (e.g., header button)
    if (
      appliedFilters === null &&
      pendingFilters === null &&
      activeFilterCount > 0 &&
      !isLoadingFromExternal.current
    ) {
      // Clear all was called from outside, reset local state without triggering context clear again
      const emptyFilters = createEmptyFilters();
      setAssetCode(emptyFilters.auditTrailAssetCode ?? '');
      setAssetDescription(emptyFilters.auditTrailAssetDescription ?? '');
      setDateRange(emptyFilters.auditTrailDateRange ?? { from: undefined, to: undefined });
      setSelectedActionTypes(emptyFilters.auditTrailActionTypes ?? []);
      setSelectedUsers(emptyFilters.auditTrailUsers ?? []);
      setSelectedSavedFilterId(null);
    }
  }, [appliedFilters, pendingFilters, activeFilterCount]);

  const hasFilterChanges = () => {
    if (!appliedFilters && activeFilterCount === 0) return false;
    if (!appliedFilters && activeFilterCount > 0) return true;
    if (!pendingFilters) return false;

    // Deep comparison
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleSaveAndApply = () => {
    if (activeFilterCount === 0) {
      toast.error('Please configure at least one filter before saving');
      return;
    }
    setSaveModalOpen(true);
  };

  const handleSaveFilter = async (name: string) => {
    if (!pendingFilters) return;

    setIsSaving(true);
    try {
      if (editingFilter) {
        const updatedFilter = await savedFiltersService.updateSavedFilter(
          editingFilter.id,
          name,
          pendingFilters
        );
        if (updatedFilter) {
          const updatedFilters = savedFilters.map((filter) =>
            filter.id === editingFilter.id ? updatedFilter : filter
          );
          setSavedFilters(updatedFilters);
          toast.success(`Filter '${name}' updated successfully`);
        } else {
          toast.error('Filter not found');
        }
        setEditingFilter(null);
      } else {
        const newFilter = await savedFiltersService.createSavedFilter(name, pendingFilters);
        setSavedFilters([...savedFilters, newFilter]);
        toast.success(`Filter '${name}' saved successfully`);
        setSelectedSavedFilterId(newFilter.id);
      }
      applyFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSavedFilter = (filterId: string | null) => {
    setSelectedSavedFilterId(filterId);
    if (filterId) {
      loadSavedFilter(filterId);
    } else {
      // Clear selection - show "Custom" state
      handleClearAll();
    }
  };

  const handleEditFilter = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setSaveModalOpen(true);
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      const success = await savedFiltersService.deleteSavedFilter(filterId);
      if (success) {
        setSavedFilters(savedFilters.filter((f) => f.id !== filterId));
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        toast.success('Filter deleted successfully');
      } else {
        toast.error('Filter not found');
      }
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  const handleDuplicateFilter = async (filter: SavedFilter) => {
    try {
      const duplicatedFilter = await savedFiltersService.createSavedFilter(
        `${filter.name} (Copy)`,
        filter.filters
      );
      setSavedFilters([...savedFilters, duplicatedFilter]);
      toast.success(`Filter '${duplicatedFilter.name}' created`);
      setSelectedSavedFilterId(duplicatedFilter.id);
      loadSavedFilter(duplicatedFilter.id);
    } catch (error) {
      console.error('Failed to duplicate filter:', error);
      toast.error('Failed to duplicate filter');
    }
  };

  const handleClearAll = () => {
    const emptyFilters = createEmptyFilters();
    setAssetCode(emptyFilters.auditTrailAssetCode ?? '');
    setAssetDescription(emptyFilters.auditTrailAssetDescription ?? '');
    setDateRange(emptyFilters.auditTrailDateRange ?? { from: undefined, to: undefined });
    setSelectedActionTypes(emptyFilters.auditTrailActionTypes ?? []);
    setSelectedUsers(emptyFilters.auditTrailUsers ?? []);
    setSelectedSavedFilterId(null);
    clearPendingFilters();
  };

  // Detect if current filters match a saved filter
  useEffect(() => {
    if (!pendingFilters || activeFilterCount === 0) {
      if (selectedSavedFilterId) {
        setSelectedSavedFilterId(null);
      }
      return;
    }

    // Check if current filters match the selected saved filter
    if (selectedSavedFilterId) {
      const savedFilter = savedFilters.find((f) => f.id === selectedSavedFilterId);
      if (savedFilter && JSON.stringify(savedFilter.filters) !== JSON.stringify(pendingFilters)) {
        // Filters have been modified, clear selection to show "Custom"
        setSelectedSavedFilterId(null);
      }
    }
  }, [pendingFilters, activeFilterCount, selectedSavedFilterId, savedFilters]);

  const hasUnsavedChanges = hasFilterChanges();

  return (
    <div className="flex flex-col h-full">
      {/* Saved Filters Dropdown - Full Width, Outside Padding */}
      <div className="border-b bg-[#f8fafc] dark:bg-[#0f172b]" style={{ borderColor: '#eee' }}>
        <SavedFiltersDropdown
          savedFilters={savedFilters}
          selectedFilterId={selectedSavedFilterId}
          onSelectFilter={handleSelectSavedFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onDuplicateFilter={handleDuplicateFilter}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">
        {/* Asset Code Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Asset Code</Label>
              {assetCode && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  Set
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by specific asset code</p>
          </div>

          <Input
            placeholder="Enter asset code..."
            value={assetCode}
            onChange={(e) => setAssetCode(e.target.value)}
          />
        </div>

        <Separator />

        {/* Asset Description Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Asset Description</Label>
              {assetDescription && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  Set
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by asset description</p>
          </div>

          <Input
            placeholder="Enter asset description..."
            value={assetDescription}
            onChange={(e) => setAssetDescription(e.target.value)}
          />
        </div>

        <Separator />

        {/* Date Range Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Date Range</Label>
              {(dateRange.from || dateRange.to) && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  Set
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by audit log date range</p>
          </div>

          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={(range) => setDateRange({ from: range?.from, to: range?.to })}
          />
        </div>

        <Separator />

        {/* Action Type Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Action Type</Label>
              {selectedActionTypes.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedActionTypes.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by type of action performed</p>
          </div>

          <MultiSelect
            options={ACTION_TYPES.map((type) => ({ label: type, value: type }))}
            value={selectedActionTypes}
            onValueChange={setSelectedActionTypes}
            placeholder="Select action types..."
          />
        </div>

        <Separator />

        {/* User Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">User</Label>
              {selectedUsers.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedUsers.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by user who performed action</p>
          </div>

          <MultiSelect
            options={USERS.map((user) => ({ label: user, value: user }))}
            value={selectedUsers}
            onValueChange={setSelectedUsers}
            placeholder="Select users..."
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-background border-t shadow-lg p-4">
        {hasUnsavedChanges && (
          <div className="mb-3 text-center">
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndApply}
            disabled={activeFilterCount === 0 || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Saving...' : 'Save and Apply'}
          </Button>
          <Button onClick={handleApplyFilters} disabled={activeFilterCount === 0} className="flex-1">
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Save Filter Modal */}
      {pendingFilters && (
        <SaveFilterModal
          open={saveModalOpen}
          onOpenChange={setSaveModalOpen}
          pendingFilters={pendingFilters}
          existingFilters={savedFilters}
          onSave={handleSaveFilter}
          editingFilter={editingFilter}
        />
      )}
    </div>
  );
};
